import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import NightsStayRoundedIcon from "@mui/icons-material/NightsStayRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useGlobalOrder } from "../../utils/GlobalStateContext";
import {
  resolveAllowedPositions,
  resolveDefaultPosition,
} from "./sopDefaults";

const parseHm = (value) => {
  if (!value) return null;
  const [h, m] = String(value).split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h * 60 + (m || 0);
};

const todayKey = () => {
  const now = new Date();
  return [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, "0"),
    String(now.getDate()).padStart(2, "0"),
  ].join("");
};

export default function SopReminder() {
  const { systemData } = useGlobalOrder();
  const navigate = useNavigate();
  const location = useLocation();
  const [prompt, setPrompt] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!systemData) return;
    if (!systemData?.sop?.auto_notify) return;
    if (!systemData?.business_hours) return;

    const check = () => {
      // 使用者正在 SOP 頁面就不重複提醒
      if (location.pathname === "/sop") return;

      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      const day = todayKey();

      const openMin = parseHm(systemData.business_hours?.open_time);
      const closeMin = parseHm(systemData.business_hours?.close_time);
      const openBefore = systemData.sop?.open_notify_before_minutes ?? 30;
      const closeBefore = systemData.sop?.close_notify_before_minutes ?? 60;
      const prepMin =
        parseHm(systemData.sop?.prep_notify_time || "14:00") ?? 14 * 60;
      const account = localStorage.getItem("account") || "";
      const allowedPositions = resolveAllowedPositions(
        account,
        systemData.sop?.allowed_positions
      );
      const defaultPosition = resolveDefaultPosition(
        account,
        systemData.sop?.default_position,
        systemData.sop?.allowed_positions
      );

      const tryFire = (stage, fromMin, toMin) => {
        if (fromMin === null || toMin === null) return;
        if (minutes < fromMin || minutes >= toMin) return;
        const flag = `sop_notified_${day}_${stage}`;
        if (localStorage.getItem(flag)) return;
        localStorage.setItem(flag, "1");
        const label = stage === "open" ? "開店" : "閉店";
        const remain = Math.max(toMin - minutes, 0);
        toast.info(
          `${label}前 ${remain} 分鐘，提醒前往執行 ${label} 作業檢核`,
          { autoClose: 8000 }
        );
        setPrompt({
          stage,
          position: defaultPosition,
          remain,
          positionLocked: allowedPositions.length === 1,
        });
      };

      if (openMin !== null) {
        tryFire("open", openMin - openBefore, openMin);
      }
      if (closeMin !== null) {
        tryFire("close", closeMin - closeBefore, closeMin);
      }

      // 14:00 正常觸發；若平板休眠或頁面稍後才開啟，18:00 前補提醒一次。
      if (
        !systemData.business_hours?.is_closed_today &&
        minutes >= prepMin &&
        minutes < 18 * 60
      ) {
        const flag = `sop_notified_${day}_prep`;
        if (!localStorage.getItem(flag)) {
          localStorage.setItem(flag, "1");
          toast.info("14:00 備置檢核提醒，請確認今日備置作業", {
            autoClose: 8000,
          });
          setPrompt({
            stage: "prep",
            position: "shared",
            remain: null,
            positionLocked: true,
          });
        }
      }
    };

    check();
    timerRef.current = setInterval(check, 60 * 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [
    systemData,
    systemData?.sop?.auto_notify,
    systemData?.business_hours?.open_time,
    systemData?.business_hours?.close_time,
    systemData?.business_hours?.is_closed_today,
    systemData?.sop?.prep_notify_time,
    location.pathname,
  ]);

  const close = () => setPrompt(null);
  const handleGo = () => {
    const search = new URLSearchParams({
      stage: prompt.stage,
      position: prompt.position,
    }).toString();
    setPrompt(null);
    navigate(`/sop?${search}`);
  };

  if (!prompt) return null;
  const stageMeta = {
    open: {
      label: "開店",
      icon: <StorefrontRoundedIcon />,
      backgroundColor: "#FFF1E5",
      color: "#C25C00",
    },
    prep: {
      label: "備置",
      icon: <Inventory2RoundedIcon />,
      backgroundColor: "#E6F4EA",
      color: "#1F8F4E",
    },
    close: {
      label: "閉店",
      icon: <NightsStayRoundedIcon />,
      backgroundColor: "#E6EBFA",
      color: "#2D3FA2",
    },
  }[prompt.stage];

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "12px",
            backgroundColor: stageMeta.backgroundColor,
            color: stageMeta.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {stageMeta.icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
            {stageMeta.label}作業檢核提醒
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#536073" }}>
            {prompt.stage === "prep"
              ? "每日 14:00 定時提醒"
              : `剩餘 ${prompt.remain} 分鐘`}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 15, color: "#202124", lineHeight: 1.7 }}>
          請依照{stageMeta.label}作業檢核項目完成檢查
          {prompt.stage === "prep" ? "，每個項目完成時選擇實際執行人。" : "。"}
          系統會自動帶入
          {prompt.stage === "prep"
            ? "共用清單。"
            : `${prompt.position === "outdoor" ? "外場" : "內場"}清單${
                prompt.positionLocked ? "。" : "，可於頁面中切換。"
              }`}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} sx={{ color: "#536073", fontWeight: 700 }}>
          稍後再說
        </Button>
        <Button
          variant="contained"
          onClick={handleGo}
          sx={{
            backgroundColor: "#1C2D42",
            color: "#FFF",
            fontWeight: 800,
            borderRadius: "999px",
            px: 3,
            "&:hover": { backgroundColor: "#0C1625" },
          }}
        >
          前往 SOP
        </Button>
      </DialogActions>
    </Dialog>
  );
}
